<script lang="ts" setup>
import BaseGrid from '@/common/components/molecules/grid/BaseGrid.vue';
import { Emit, Props, PROPS_DEFAULTS, setup } from '@/alert/components/alertGrid/alertGrid.setup';
import { isEventType } from '@/infrastructure/views/networkDeviceView/networkDeviceDetail/networkDeviceAlert/networkDeviceAlert.setup';
import AlertTargetList from '@/alert/components/alertTargetList/AlertTargetList.vue';
import { makeRowToObj } from '@/common/utils/gridUtils';
import { ALERT_RULE_NAME_ICON } from '@/alert/utils/tagsAndTargets.uses';
import SimpleTextTooltip from '@/common/components/molecules/simpleTextTooltip/SimpleTextTooltip.vue';
import SetupDashBoardPopup from '@/common/components/organisms/setupDashBoardPopup/SetupDashBoardPopup.vue';
import BaseCustomTooltip from '@/common/components/molecules/baseCustomTooltip/BaseCustomTooltip.vue';
import AlertStatusButtonRow from '@/alert/components/alertStatusButtonRow/AlertStatusButtonRow.vue';
import { resolveAlertValueViewType } from '@/alert/components/alertGrid/alertGrid.utils';
import TextViewerWindow from '@/common/components/organisms/textViewerWindow/TextViewerWindow.vue';
import StatusTag from '@/common/components/molecules/statusTag/StatusTag.vue';
import { formatNumberToLocale } from '@/common/utils/commonUtils';
import { isNil } from 'lodash-es';
import TagItem from '@/common/components/molecules/tag/TagItem.vue';
import DashBoardListPopup from '@/common/components/organisms/dashBoardListPopup/DashBoardListPopup.vue';
import LogAlertViewerWindow from '@/alert/components/logAlertViewerWindow/LogAlertViewerWindow.vue';
import { EVENT_TYPE_ENUM } from '@/alert/utils/define';
import HelpTooltip from '@/common/components/molecules/HelpTooltip/HelpTooltip.vue';

const props = withDefaults(defineProps<Props>(), { ...PROPS_DEFAULTS });
const emit = defineEmits<Emit>();

const {
  gridOption,
  searchBarOption,
  checkedRows,
  searchWord,
  exportExcelOption,
  columnIdx,
  t,
  alertName,

  criteriaTooltipMouseEvent,
  criteriaTooltipText,
  onCriteriaMouse,

  isVisibleSetupDashBoard,
  dashboardListRef,
  dashboardEnvKey,
  setupDashboardPopupClose,
  viewDashboardList,
  moveDashboard,
  dashboardName,
  dashboardPopupListCnt,
  dashboardPopupList,
  dashboardTooltipRef,
  onShowDashboardTooltip,
  onHideDashboardTooltip,
  onClickCellByField,

  valueModalInfo,
  onClickLinkedValue,
  getAlertValue,
  getLoggingAlertValue,
} = setup(props, emit);
</script>

<template>
  <base-grid
    v-bind="$attrs"
    v-model:checked-rows="checkedRows"
    v-model:search-word="searchWord"
    :type="props.type === 'main' ? 'page' : 'widget'"
    :columns="props.gridColumns"
    :option="gridOption"
    :search-bar-option="searchBarOption"
    :rows="props.gridData"
    class="alert-grid"
    :export-excel-option="exportExcelOption"
    :column-env-key="`${props.envKey}_grid`"
    @click-cell="
      (e) =>
        emit('on-click-cell', {
          ...e,
          row: makeRowToObj(e.row, props.gridColumns),
        })
    "
    @export-to-excel-from-server="
      ({ columns }) => emit('export-to-excel-from-server', { ...exportExcelOption, columns })
    "
  >
    <template #alertRuleName="{ item }">
      <div
        v-if="item.row[2][item.column.index] === ALERT_RULE_NAME_ICON"
        class="alert-grid__child-icon"
      >
        <ev-icon :icon="ALERT_RULE_NAME_ICON" />
      </div>
      <div
        v-else
        :class="[
          'alert-grid__rule-name',
          props.gridColumns[columnIdx.alertRuleName].clickable ? 'base-grid__cell--link' : '',
        ]"
        @click.stop="
          () => {
            if (props.gridColumns[columnIdx.alertRuleName].clickable) {
              onClickCellByField(
                'alertRuleName',
                item.row[2][item.column.index],
                makeRowToObj(item.row[2], props.gridColumns),
                item.column.index,
              );
            }
          }
        "
      >
        <template v-if="columnIdx.ruleNameIcon && item.row[2][columnIdx.ruleNameIcon]">
          <ev-icon
            :icon="item.row[2][columnIdx.ruleNameIcon].iconClass"
            @mouseenter="
              (e) => onCriteriaMouse({ text: item.row[2][columnIdx.ruleNameIcon].tooltip, e })
            "
            @mouseleave="(e) => onCriteriaMouse({ text: '', e })"
          />
        </template>
        <span :title="item.row[2][item.column.index]">{{ item.row[2][item.column.index] }}</span>
      </div>
    </template>
    <template #tags="{ item }">
      <alert-target-list
        v-if="item.row[2][item.column.index]?.length"
        :target-list="item.row[2][item.column.index]"
        :shown-count="1"
      />
    </template>
    <template #targets="{ item }">
      <alert-target-list
        v-if="item.row[2][item.column.index]?.length"
        :row-data="makeRowToObj(item.row[2], props.gridColumns)"
        :target-list="item.row[2][item.column.index]"
        :is-clickable="props.isTargetClickable"
        :shown-count="1"
        :time-period-info="props.timePeriodInfo"
      />
    </template>
    <template #threshold="{ item }">
      <div
        v-if="item.row[2][item.column.index]"
        class="threshold"
      >
        <span>{{ alertName(item.row[2][item.column.index].alertName) }}</span>
        <span>
          <strong>{{ item.row[2][item.column.index].evaluateValue }}</strong>
        </span>
        <span
          v-if="item.row[2][item.column.index].duringTimeInterval"
          class="alert-grid__support-word"
        >
          {{ t('WORD.DURING_THE') }}
        </span>
        <span>
          <strong>{{ item.row[2][item.column.index].duringTimeInterval }}</strong>
        </span>
        <span>
          <strong>{{
            item.row[2][item.column.index].criticalComparison ||
            item.row[2][item.column.index].warningComparison
          }}</strong>
        </span>
        <status-tag
          v-if="!isNil(item.row[2][item.column.index].warning)"
          type="warning"
          class="auto-width"
          >warning</status-tag
        >
        <span>
          <strong>{{ formatNumberToLocale(item.row[2][item.column.index].warning) }}</strong>
          <template v-if="!isNil(item.row[2][item.column.index].warning)">
            {{ item.row[2][columnIdx.statUnit] }}
          </template>
        </span>
        <status-tag
          type="critical"
          class="auto-width"
          >critical</status-tag
        >
        <span>
          <strong>
            {{ formatNumberToLocale(item.row[2][item.column.index].critical) }}
          </strong>
          <template v-if="!isNil(item.row[2][item.column.index].critical)">
            {{ item.row[2][columnIdx.statUnit] }}
          </template>
        </span>
      </div>
    </template>
    <template #value="{ item }">
      <template v-if="isEventType(item.row[2][item.column.index])">
        <div
          class="base-grid__cell--link"
          @click.stop="
            () =>
              onClickCellByField(
                'value',
                item.row[2][item.column.index],
                makeRowToObj(item.row[2], props.gridColumns),
                item.column.index,
              )
          "
        >
          <span
            v-if="item.row[2][item.column.index]"
            :title="item.row[2][item.column.index]"
            class="cell-text"
          >
            {{ item.row[2][item.column.index] }}
          </span>
        </div>
      </template>

      <template
        v-else-if="
          resolveAlertValueViewType(makeRowToObj(item.row[2], props.gridColumns)) === 'text'
        "
      >
        <div class="base-grid__cell--link linked-value">
          <span
            :title="
              getAlertValue(
                item.row[2][item.column.index],
                makeRowToObj(item.row[2], props.gridColumns),
                item.row?.[2]?.[columnIdx.statUnit],
              )
            "
            class="cell-text"
            @click.stop="() => onClickLinkedValue(makeRowToObj(item.row[2], props.gridColumns))"
          >
            {{
              getAlertValue(
                item.row[2][item.column.index],
                makeRowToObj(item.row[2], props.gridColumns),
                item.row?.[2]?.[columnIdx.statUnit],
              )
            }}
          </span>
        </div>
      </template>

      <template
        v-else-if="
          resolveAlertValueViewType(makeRowToObj(item.row[2], props.gridColumns)) === 'logging'
        "
      >
        <p
          v-dompurify-html="getLoggingAlertValue(item.row[2][item.column.index]) || ''"
          class="alert-grid__log-content"
          @click.stop="() => onClickLinkedValue(makeRowToObj(item.row[2], props.gridColumns))"
        />
      </template>

      <template v-else>
        <span
          :title="
            getAlertValue(
              item.row[2][item.column.index],
              makeRowToObj(item.row[2], props.gridColumns),
              item.row?.[2]?.[columnIdx.statUnit],
            )
          "
          class="alert-grid__value-text"
        >
          {{
            getAlertValue(
              item.row[2][item.column.index],
              makeRowToObj(item.row[2], props.gridColumns),
              item.row?.[2]?.[columnIdx.statUnit],
            )
          }}
        </span>
      </template>
    </template>
    <template #detail="{ item }">
      <tag-item
        html-type="button"
        icon="icon-arrow-right"
        size="small"
        class="border-button"
        :disabled="dashboardPopupListCnt === 0"
        @click.stop="(e) => viewDashboardList(e, makeRowToObj(item.row[2], props.gridColumns))"
        @mouseenter="(e) => onShowDashboardTooltip(e)"
        @mouseleave="(e) => onHideDashboardTooltip(e)"
      >
        {{ dashboardName === '' ? t('WORD.VIEW_DETAIL') : dashboardName }}
      </tag-item>
    </template>
    <template #notificationStatus="{ item }">
      <alert-status-button-row
        :status-list="item.row[2]?.[item.column.index] || []"
        :row-index="item.row[0]"
        @click-status-button="
          (iconType) =>
            emit('click-status-button', {
              iconType,
              row: makeRowToObj(item.row[2], props.gridColumns),
            })
        "
      />
    </template>
    <template
      v-for="slotField in Object.keys($slots)"
      :key="slotField"
      #[slotField]="{ item }"
    >
      <slot
        :name="slotField"
        :item="item"
      />
    </template>
  </base-grid>
  <simple-text-tooltip
    :mouse-event="criteriaTooltipMouseEvent"
    :text="criteriaTooltipText"
    base-pos="bottom"
  />
  <base-custom-tooltip
    ref="dashboardTooltipRef"
    custom-class="dashboard-msg-tooltip"
    use-deselect
    use-hover-event
  >
    <p>{{ t('MESSAGE.NO_PAGE_DASHBOARD') }}</p>
  </base-custom-tooltip>
  <setup-dash-board-popup
    v-if="props.type === 'detail' && isVisibleSetupDashBoard"
    v-model:is-show="isVisibleSetupDashBoard"
    :env-key="dashboardEnvKey"
    :is-alert="true"
    @close="setupDashboardPopupClose"
  />
  <base-custom-tooltip
    ref="dashboardListRef"
    custom-class="dashboard-list-tooltip"
    use-deselect
  >
    <dash-board-list-popup
      :move-dashboard="moveDashboard"
      :dashboard-list="dashboardPopupList"
    />
  </base-custom-tooltip>
  <text-viewer-window
    v-if="valueModalInfo.windowType === 'text'"
    v-model:is-show="valueModalInfo.isShow"
    title="Alert Value"
    :type="valueModalInfo.type"
    :text="valueModalInfo.text"
    :show-message-title="false"
    :line-number="false"
  >
    <template
      v-if="valueModalInfo.eventType === EVENT_TYPE_ENUM.APPLICATION"
      #header-icon
    >
      <help-tooltip
        pre-line
        custom-tooltip-class="alert-value-limit"
      >
        {{ t('MESSAGE.APP_ALERT_VALUE_LEN_LIMIT') }}
      </help-tooltip>
    </template>
  </text-viewer-window>
  <log-alert-viewer-window
    v-if="valueModalInfo.windowType === 'logging'"
    v-model:is-show="valueModalInfo.isShow"
    :target-id="valueModalInfo.targetId"
    :rule-id="valueModalInfo.ruleId"
    :result-id="valueModalInfo.resultId"
    :from-time="valueModalInfo.fromTime"
    :to-time="valueModalInfo.toTime"
  />
</template>

<style lang="scss">
.alert-grid {
  .list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    line-height: 20px;

    &__title {
      font-size: 14px;
      font-weight: 700;
    }
  }
  .border-button {
    height: 20px !important;
  }
  .base-grid__chips {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
  .threshold {
    > * + * {
      margin-left: 4px;
    }

    > * {
      vertical-align: middle;
    }

    .auto-width {
      min-width: unset;
      padding-right: 6px;
      padding-left: 6px;
    }

    &.strong {
      font-weight: 700;
      color: var(--main-font-color);
    }
  }
  &.alert-current,
  &.alert-history {
    .status-tag {
      min-width: 48px;
      padding: 2px 6px 1px;
    }
  }
  .target-list {
    gap: 4px;
    justify-content: center;
    .target-item-wrapper {
      width: auto;
    }
    .target-item:last-child {
      margin-right: 0;
    }
    .more-btn {
      margin-left: 0;
    }
  }
  &__child-icon {
    display: flex;
    align-items: center;
    justify-content: end;
    height: 100%;
  }
  &__rule-name {
    i {
      vertical-align: middle;
      background-color: var(--alert-detail-item-rule-criteria-color);
    }
  }
  &__support-word {
    color: var(--color-gray-02-10);
  }
  &__log-content {
    width: 100%;
    cursor: pointer;

    @include shortening();
  }
  &__value-text {
    @include shortening();
  }
  &.base-tree-grid .ev-checkbox.disabled {
    display: none !important;
  }

  .ev-grid-toggle {
    margin-right: 4px;
  }
}
.alert-detail .alert-grid,
.event-alert-detail .alert-grid {
  .grid-toolbar {
    flex-direction: row;
    &__row.search {
      width: calc(100% - 36px);
    }
    &__row.custom.with-grid-setting {
      width: auto !important;
    }

    .ev-text-field {
      width: 100% !important;
    }
  }
  .tree-grid-toolbar {
    .grid-toolbar {
      align-items: center;

      &__row {
        display: flex;
        align-items: center;
      }
    }
  }
  &.base-tree-grid.has-option-h2,
  &.base-grid.has-option-h2 {
    .toolbar-wrapper + div {
      height: calc(100% - var(--option-height-h1)) !important;
    }
  }
}
.dashboard-msg-tooltip {
  width: 150px;
  max-height: 290px;
  padding: 6px 8px;
  background-color: var(--alert-grid-tooltip-bg);
  font-size: 11px;
  color: var(--alert-grid-tooltip-color);
}
.action-cell-context-alert {
  .ev-menu-ul {
    max-height: 200px;
    overflow-y: auto;
  }

  @include set-scrollbar();
}
.alert-value-limit {
  width: 300px;
}
</style>
